import { DeleteOutlined, DragOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  FALLBACK_CURRENCY,
  fetchEventCurrency as fetchEventCurrencyFromApi,
} from '../config/eventCurrency';

const { Option } = Select;

interface SponsorLevel {
  id: string;
  name: string;
  level: number;
  color?: string;
  benefits?: string;
  minAmount?: number;
  maxSponsors?: number;
  eventId: string;
}

interface EventSponsorLevelsProps {
  event: {
    id: string;
    title: string;
  };
  visible: boolean;
  onClose: () => void;
}

// Default sponsor levels.
//
// These names are NOT translated on purpose. They are written to the database
// as `EventSponsorLevel.name` and rendered on the public sponsor page, whose
// language is `Event.language` — not the language of the admin panel. Running
// them through `t()` would let an organizer working in English stamp English
// tier names onto a Hungarian event's public page. Making them follow the
// event's language is the correct fix, and needs the language threaded into
// this component; until then they stay as they have always been.
const DEFAULT_LEVELS = [
  { name: 'Platina', level: 1, color: '#E5E4E2', minAmount: 50000 },
  { name: 'Arany', level: 2, color: '#FFD700', minAmount: 30000 },
  { name: 'Ezüst', level: 3, color: '#C0C0C0', minAmount: 15000 },
  { name: 'Bronz', level: 4, color: '#CD7F32', minAmount: 5000 },
  { name: 'Partner', level: 5, color: '#6B7280', minAmount: 1000 },
];

const EventSponsorLevels: React.FC<EventSponsorLevelsProps> = ({ event, visible, onClose }) => {
  const [sponsorLevels, setSponsorLevels] = useState<SponsorLevel[]>([]);
  const [currency, setCurrency] = useState<string>('EUR');
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLevel, setEditingLevel] = useState<SponsorLevel | null>(null);
  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    if (visible && event) {
      fetchSponsorLevels();
      fetchEventCurrency();
    }
  }, [visible, event]);

  /**
   * A pénznem a közös segédből (#111 utómunka) — korábban ez a másolat egy volt
   * a négyből. A megjelenítési tartalék itt marad `EUR`: egy szerkesztő űrlap
   * mezőjének mindenképp kell egy felirat.
   */
  const fetchEventCurrency = async () => {
    if (!event?.id) return;
    setCurrency((await fetchEventCurrencyFromApi(event.id)) ?? FALLBACK_CURRENCY);
  };

  const fetchSponsorLevels = async () => {
    if (!event?.id) {
      console.error('No event ID provided');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/events/${event.id}/sponsor-levels`);
      const levels = response.data || [];
      setSponsorLevels(levels);

      // If no levels exist, offer to create defaults
      if (levels.length === 0) {
        const shouldCreate = window.confirm(t('sponsorLevels.confirmDefaults'));
        if (shouldCreate) {
          await createDefaultLevels();
        }
      }
    } catch (error: any) {
      console.error('Error fetching sponsor levels:', error.response?.data);

      // Only try to create defaults on 404 or empty result
      if (error.response?.status === 404) {
        setSponsorLevels([]);
        const shouldCreate = window.confirm(t('sponsorLevels.confirmDefaults'));
        if (shouldCreate) {
          await createDefaultLevels();
        }
      } else {
        message.error(t('sponsorLevels.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const createDefaultLevels = async () => {
    try {
      // Create levels sequentially to avoid conflicts
      for (const level of DEFAULT_LEVELS) {
        try {
          await api.post(`/events/${event.id}/sponsor-levels`, level);
        } catch (err: any) {
          console.error(`Failed to create level ${level.name}:`, err.response?.data);
        }
      }
      message.success(t('sponsorLevels.defaultsCreated'));
      fetchSponsorLevels();
    } catch (error) {
      console.error('Error creating default levels:', error);
      message.error(t('sponsorLevels.defaultsFailed'));
    }
  };

  const handleCreate = () => {
    setEditingLevel(null);
    form.resetFields();
    form.setFieldsValue({
      level: sponsorLevels.length + 1,
      color: '#6B7280',
    });
    setModalVisible(true);
  };

  const handleEdit = (record: SponsorLevel) => {
    setEditingLevel(record);
    // Először resetelünk, hogy az új mezők (baseTicketCount, extraTicketsAllowed)
    // initialValue-ja érvényesüljön a régi rekordoknál, amelyekben még nincsenek.
    form.resetFields();
    form.setFieldsValue({
      ...record,
      baseTicketCount: (record as any).baseTicketCount ?? 1,
      extraTicketsAllowed: (record as any).extraTicketsAllowed ?? 0,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/events/${event.id}/sponsor-levels/${id}`);
      message.success(t('sponsorLevels.deleted'));
      fetchSponsorLevels();
    } catch (error) {
      message.error(t('sponsorLevels.deleteFailed'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // A backend DTO `forbidNonWhitelisted: true`-val fut — csak az engedélyezett
      // mezőket küldjük el, és csakis akkor, ha tényleg van értékük.
      // (A null értékek a class-validator `@IsInt()`-jén bukhatnak.)
      const cleaned: Record<string, any> = {};
      const optional = [
        'color',
        'benefits',
        'minAmount',
        'maxSponsors',
        'baseTicketCount',
        'extraTicketsAllowed',
      ];
      cleaned.name = values.name;
      cleaned.level = values.level;
      for (const k of optional) {
        if (values[k] !== undefined && values[k] !== null && values[k] !== '') {
          cleaned[k] = values[k];
        }
      }
      if (editingLevel) {
        await api.patch(`/events/${event.id}/sponsor-levels/${editingLevel.id}`, cleaned);
        message.success(t('sponsorLevels.updated'));
      } else {
        await api.post(`/events/${event.id}/sponsor-levels`, cleaned);
        message.success(t('sponsorLevels.created'));
      }
      setModalVisible(false);
      fetchSponsorLevels();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('SponsorLevel save error response:', error.response?.data);
      const msg = extractErrorMessage(error);
      message.error(msg || t('sponsorLevels.operationFailed'));
    }
  };

  // A backend AllExceptionsFilter a ValidationPipe response-t a `message` mezőbe
  // teszi mint object: { message: [...], error: 'Bad Request', statusCode }.
  // Innen ki kell venni a tényleges string(eke)t, hogy az AntD message.error
  // ne próbáljon object-et React-children-ként rendereli.
  const extractErrorMessage = (error: any): string => {
    const data = error?.response?.data;
    if (!data) return error?.message || t('sponsorLevels.unknownError');
    const m = data.message;
    if (typeof m === 'string') return m;
    if (Array.isArray(m)) return m.join(', ');
    if (m && typeof m === 'object') {
      const inner = m.message;
      if (typeof inner === 'string') return inner;
      if (Array.isArray(inner)) return inner.join(', ');
      return JSON.stringify(m);
    }
    return JSON.stringify(data);
  };

  const columns: ColumnsType<SponsorLevel> = [
    {
      title: t('sponsorLevels.columnOrder'),
      dataIndex: 'level',
      key: 'level',
      width: 80,
      sorter: (a, b) => a.level - b.level,
      defaultSortOrder: 'ascend',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {record.color && (
            <div
              style={{
                width: 20,
                height: 20,
                backgroundColor: record.color,
                borderRadius: 4,
                border: '1px solid #d9d9d9',
              }}
            />
          )}
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: t('sponsorLevels.columnMinAmount'),
      dataIndex: 'minAmount',
      key: 'minAmount',
      render: (amount) => (amount ? `${amount.toFixed(2)} ${currency}` : '-'),
    },
    {
      title: t('sponsorLevels.columnMaxSponsors'),
      dataIndex: 'maxSponsors',
      key: 'maxSponsors',
      render: (max) => max || t('sponsorLevels.unlimited'),
    },
    {
      title: t('sponsorLevels.columnTickets'),
      key: 'tickets',
      render: (_, record: any) => {
        const base = record.baseTicketCount ?? 1;
        const extra = record.extraTicketsAllowed ?? 0;
        return extra > 0 ? t('sponsorLevels.ticketsWithExtra', { base, extra }) : `${base}`;
      },
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={t('sponsorLevels.manageTitle', { event: event?.title })}
        open={visible}
        onCancel={onClose}
        width={800}
        footer={[
          <Button key="close" onClick={onClose}>
            {t('common.close')}
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('sponsorLevels.newLevel')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={sponsorLevels}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Modal>

      <Modal
        title={editingLevel ? t('sponsorLevels.editTitle') : t('sponsorLevels.newLevel')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label={t('common.name')}
            rules={[{ required: true, message: t('common.requiredField') }]}
          >
            <Input placeholder={t('sponsorLevels.namePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="level"
            label={t('sponsorLevels.columnOrder')}
            rules={[{ required: true, message: t('common.requiredField') }]}
            tooltip={t('sponsorLevels.orderTooltip')}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="color" label={t('sponsorLevels.colorLabel')}>
            <Input
              placeholder={t('sponsorLevels.colorPlaceholder')}
              addonBefore={
                <div
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: form.getFieldValue('color') || '#ccc',
                    border: '1px solid #d9d9d9',
                    borderRadius: 2,
                  }}
                />
              }
            />
          </Form.Item>

          <Form.Item name="minAmount" label={t('sponsorLevels.minAmountLabel', { currency })}>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              addonAfter={currency}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '') as any}
            />
          </Form.Item>

          <Form.Item
            name="baseTicketCount"
            label={t('sponsorLevels.baseTicketsLabel')}
            tooltip={t('sponsorLevels.baseTicketsTooltip')}
            initialValue={1}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              addonAfter={t('sponsorLevels.ticketUnit')}
            />
          </Form.Item>

          <Form.Item
            name="extraTicketsAllowed"
            label={t('sponsorLevels.extraTicketsLabel')}
            tooltip={t('sponsorLevels.extraTicketsTooltip')}
            initialValue={0}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              addonAfter={t('sponsorLevels.ticketUnit')}
            />
          </Form.Item>

          <Form.Item
            name="maxSponsors"
            label={t('sponsorLevels.maxSponsorsLabel')}
            tooltip={t('sponsorLevels.maxSponsorsTooltip')}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="benefits" label={t('sponsorLevels.benefitsLabel')}>
            <Input.TextArea rows={4} placeholder={t('sponsorLevels.benefitsPlaceholder')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                onClick={async () => {
                  try {
                    const values = await form.validateFields();
                    await handleSubmit(values);
                  } catch (e: any) {
                    if (e?.errorFields?.length) {
                      const messages = e.errorFields.flatMap((f: any) => f.errors).join(', ');
                      message.error(t('sponsorLevels.fillRequired', { fields: messages }));
                    } else {
                      message.error(t('sponsorLevels.saveFailedConsole'));
                      // eslint-disable-next-line no-console
                      console.error('SponsorLevel submit error:', e);
                    }
                  }
                }}
              >
                {editingLevel ? t('sponsorLevels.save') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('sponsorLevels.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EventSponsorLevels;
